import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { CheckCircle, ArrowRight, Truck, Star } from "lucide-react";

const SITE = SITE_URL;

const nunaModels = [
  {
    slug: "nuna-mixx",
    name: "Nuna MIXX Next",
    type: "Single",
    price: 26,
    highlight: "Premium all-terrain single",
    desc: "Tall adjustable handlebar, premium suspension for smooth rides over any surface, extended UPF 50+ canopy, and multiple recline positions. The luxury choice for Disney vacations.",
  },
  {
    slug: "nuna-trvl",
    name: "Nuna Travel Stroller (TRVL)",
    type: "Single",
    price: 22,
    highlight: "Compact travel stroller",
    desc: "Nuna's ultra-compact stroller folds to a fraction of its full size. Ideal for families flying to Disney who want a premium stroller experience without checking a full-size frame.",
  },
  {
    slug: "nuna-taco",
    name: "Nuna TAOS Compact",
    type: "Single",
    price: 22,
    highlight: "Lightweight compact",
    desc: "The TAOS balances Nuna's premium quality with a lighter, more compact frame. Self-standing fold, large sun canopy and a smooth ride for resort-to-park days.",
  },
];

const faqs = [
  {
    q: "What makes Nuna strollers good for Disney World?",
    a: "Nuna strollers are designed with tall handlebars, premium suspension and extended sun canopy coverage — features that matter on long Disney park days. The MIXX Next in particular is popular with taller parents who want a comfortable push over any terrain. All Nuna models fit Disney's park size requirement.",
  },
  {
    q: "Are Nuna strollers allowed in Disney theme parks?",
    a: "Yes. All three Nuna models we carry fit within Disney World's stroller size requirement (no wider than 31 inches, no longer than 52 inches). They can be used in all four Disney parks and on Disney transportation.",
  },
  {
    q: "What is the difference between the Nuna MIXX, TRVL and TAOS for Disney?",
    a: "The Nuna MIXX Next is the fullest-featured model — premium suspension, extended canopy, reversible seat and higher weight capacity. It's the luxury pick for long Disney days. The TRVL is Nuna's compact travel stroller, best for families prioritizing a small fold, though it carries slightly less weight. The TAOS is a compact daily stroller between the two — lighter than the MIXX with a self-standing fold for easy storage at the resort.",
  },
  {
    q: "Do you deliver Nuna strollers to Disney resorts?",
    a: "Yes. We deliver all Nuna stroller models to every Walt Disney World resort. All Disney resort deliveries require an in-person lobby meeting — Disney does not permit third-party vendors to leave equipment with bell services.",
  },
  {
    q: "How does the Nuna MIXX Next compare to the Baby Jogger GT2 for Disney?",
    a: "Both are excellent Disney strollers. The Nuna MIXX Next has premium suspension and a taller handlebar, making it more comfortable for taller parents over long days. The Baby Jogger GT2 has a faster one-step fold and slightly better all-terrain wheels. If comfort and style are priorities, go Nuna. If all-terrain performance and a fast fold win out, go Baby Jogger.",
  },
  {
    q: "Is delivery free for Nuna stroller rentals at Disney?",
    a: "Yes. Standard delivery is free to all Walt Disney World resorts, Universal Orlando, vacation homes and most Orlando hotels. Delivery is always free — no early-delivery surcharges.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Nuna Stroller Rental Disney World", item: `${SITE}/nuna-stroller-rental-disney-world` },
  ],
};

export default function NunaStrollerRental() {
  return (
    <>
      <PageSEO
        title="Nuna Stroller Rental Near Disney World | MIXX, TRVL & TAOS Orlando"
        description="Rent a Nuna MIXX Next, TRVL or TAOS Compact stroller near Walt Disney World. Premium suspension, tall handlebars and free delivery to Disney resorts. From $22/day. Enchanted Gear Rentals."
        canonical={`${SITE}/nuna-stroller-rental-disney-world`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Nuna Stroller Rental</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-[#FFD166]/10 border border-[#FFD166]/30 text-[#FFD166] text-sm font-medium px-4 py-1.5 rounded-full mb-5">
              <Star className="w-4 h-4" /> Premium suspension &amp; tall handlebars
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Nuna Stroller Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Nuna strollers are the premium choice for Disney families who want a smooth, well-engineered ride over theme park terrain. We rent the Nuna MIXX Next, TRVL and TAOS Compact — with free delivery to every Walt Disney World resort.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5] italic">Not affiliated with The Walt Disney Company.</p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="nuna-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/strollers"><button className="btn-ghost">Browse All Strollers</button></Link>
            </div>
          </div>
        </section>

        {/* Models */}
        <section className="container py-14">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Nuna stroller models available to rent</h2>
          <p className="text-[#B9C8D5] mb-10 max-w-2xl">All Nuna strollers are sanitized and inspected before delivery. Every model fits Disney's park stroller size limit.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {nunaModels.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-6 transition-all hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full">{s.type}</div>
                  <span className="text-xs text-[#FFD166] bg-[#FFD166]/10 px-2 py-0.5 rounded-full">{s.highlight}</span>
                </div>
                <h3 className="font-serif text-xl text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-2">{s.name}</h3>
                <p className="text-sm text-[#B9C8D5] mb-4">{s.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[#FFD166] font-semibold">From ${s.price}/day</span>
                  <span className="text-xs text-[#B9C8D5] flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Why Nuna */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">Why Nuna is popular with Disney families</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {[
              { icon: CheckCircle, title: "Premium suspension for long days", body: "Nuna's multi-position suspension absorbs Disney's varied terrain — cobblestones, pavement transitions and slight inclines — so the ride stays smooth for your child throughout the day." },
              { icon: CheckCircle, title: "Tall handlebar for comfort", body: "The Nuna MIXX Next's adjustable handlebar goes higher than most strollers in our fleet. Taller parents consistently rate it the most comfortable stroller to push for a full Disney park day." },
              { icon: Truck, title: "Free delivery to Disney resorts", body: "We deliver to every Walt Disney World resort. All Disney deliveries require an in-person lobby meeting — we cannot leave equipment at bell services." },
              { icon: Star, title: "Extended UPF 50+ sun canopy", body: "Orlando sun is intense. The Nuna MIXX Next has a large extending canopy with UPF 50+ protection — keeping your child covered while you keep moving through the parks." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <Icon className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#F6F1E4]">{title}</div>
                  <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison */}
        <section className="container py-10">
          <div className="max-w-2xl rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Which Nuna should you choose for Disney?</h2>
            <div className="space-y-4 text-sm text-[#B9C8D5]">
              <div>
                <span className="font-semibold text-[#F6F1E4]">Nuna MIXX Next</span> — Best for full-time daily use at Disney. Premium suspension, reversible seat, tallest handlebar, largest canopy. The premium choice for families spending multiple days in the parks.
              </div>
              <div>
                <span className="font-semibold text-[#F6F1E4]">Nuna TRVL</span> — Best for families who flew to Disney and want a premium compact fold. Lighter and smaller than the MIXX when folded. Great for a few park days without needing the full-size frame.
              </div>
              <div>
                <span className="font-semibold text-[#F6F1E4]">Nuna TAOS Compact</span> — The balance point between MIXX quality and TRVL portability. Self-standing fold, lighter weight, still delivers a smooth Nuna ride.
              </div>
            </div>
            <p className="mt-4 text-sm text-[#B9C8D5]">Not sure which to pick? Try our <Link to="/stroller-quiz" className="text-[#FFD166] hover:underline">stroller recommendation quiz</Link> for a personalized answer.</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Nuna stroller rental FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Compare other stroller brands</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Zoe stroller rental Disney", to: "/zoe-stroller-rental-disney-world" },
              { label: "Baby Jogger stroller rental Disney", to: "/baby-jogger-stroller-rental-disney-world" },
              { label: "Disney World stroller rentals", to: "/disney-world-stroller-rentals" },
              { label: "Single stroller rentals Orlando", to: "/single-stroller-rentals-orlando" },
              { label: "Stroller quiz", to: "/stroller-quiz" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your Nuna stroller for Disney World</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and delivery resort to check availability and confirm your Nuna stroller rental in minutes.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="nuna-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
